
import { mergeAttributes, Node } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import VideoNodeView from './VideoNodeView';

export interface VideoOptions {
  HTMLAttributes: Record<string, any>;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    video: {
      /**
       * Add a video
       */
      setVideo: (options: { src: string; platform: 'youtube' | 'vimeo'; caption?: string }) => ReturnType;
    };
  }
}

export const VideoExtension = Node.create<VideoOptions>({
  name: 'video',
  
  group: 'block',
  
  content: '',
  
  marks: '',
  
  selectable: true,
  
  draggable: true,
  
  atom: true,

  addOptions() {
    return {
      HTMLAttributes: {
        class: 'video-embed',
      },
    };
  },

  addAttributes() {
    return {
      src: {
        default: null,
      },
      platform: {
        default: 'youtube',
      },
      caption: {
        default: null,
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[class="video-embed"]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    const { src, platform, caption } = HTMLAttributes;
    
    // Create the base attributes for the main container
    const containerAttributes = mergeAttributes(this.options.HTMLAttributes, {
      'data-type': this.name,
      'data-platform': platform,
      'data-src': src
    });

    // Build the content elements
    const contentElements = [];
    
    // Create the video container
    if (platform === 'youtube' && src) {
      // YouTube embed
      const iframeAttrs = {
        width: '560',
        height: '315',
        src: `https://www.youtube.com/embed/${src}`,
        frameborder: '0',
        allow: 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture',
        allowfullscreen: 'true',
        title: 'YouTube video player'
      };
      
      contentElements.push(
        ['div', { class: 'video-container' }, 
          ['iframe', iframeAttrs]
        ]
      );
    } else if (platform === 'vimeo' && src) {
      // Vimeo embed
      const iframeAttrs = {
        src: `https://player.vimeo.com/video/${src}`,
        width: '560',
        height: '315',
        frameborder: '0',
        allow: 'autoplay; fullscreen; picture-in-picture',
        allowfullscreen: 'true',
        title: 'Vimeo video player'
      };
      
      contentElements.push(
        ['div', { class: 'video-container' }, 
          ['iframe', iframeAttrs]
        ]
      );
    } else {
      // Invalid video source
      contentElements.push(
        ['div', { class: 'video-container' }, 
          ['div', { class: 'video-error' }, 'Invalid video source']
        ]
      );
    }
    
    // Add caption if present
    if (caption) {
      contentElements.push(['p', { class: 'video-caption' }, caption]);
    }

    // Return with the correct format: [tagName, attributes, ...content]
    return ['div', containerAttributes, ...contentElements];
  },

  addNodeView() {
    return ReactNodeViewRenderer(VideoNodeView);
  },

  addCommands() {
    return {
      setVideo:
        (options) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: options,
          });
        },
    };
  },
});

export default VideoExtension;
